import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { timeoutFetchSentryGuard } from 'Fetch';
import { getVehiclePhotos, putEnquiryConfirmation } from 'PrivateAPI';

import { UserContext, useUserPartial } from 'Context/user';
import { useUpdateInfoAction } from 'Context/useUpdateInfoAction';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { SP_UPDATED_VALUATION } from 'Utilities/analytics/analyticsUpdatedValuationPage';
import { formatPhoneNumber } from 'Utilities/helpers';
import type { ImageSections, Photos, PremiumOffer, ProfileConfirmationDetails, SectionDataProps, SectionsProps, TableDataProps } from 'Utilities/profiling/@types';
import { isStatusPostQaTimeline, isStatusRejectedTimeline } from 'Utilities/restricted';
import { safeLocalStorage } from 'Utilities/storages';

import { useBreakpoint } from '../../../components/misc/Breakpoints/Breakpoints';

import { scrollToTopOfSection } from './ConfirmDetails/ConfirmDetails.helpers';
import { VehicleProfilingState } from './DataFlow/VehicleProfilingContext';
import { useVehicleProfilingContext, useVehicleProfilingData } from './DataFlow/VehicleProfilingContext.helpers';
import { getHistorySections, SERVICE_PHOTOS_KEY, SERVICE_UPLOAD_KEY } from './ServiceHistory/ServiceHistory.helpers';
import { useResendVehicleDetails, useSummarySections } from './Summary/Summary.hooks';
import {
  CONFIRM_DETAILS_SECTIONS,
  CONFIRM_PRICE_EXPIRED_ERROR_MSG,
  PROFILING_PATH,
  UPDATED_VALUATION_REQUIREMENTS,
} from './VehicleProfiling.constants';
import {
  isMustHaveProofOfPurchase, isPhotosResupply,
  isRetakeStatus, isServiceResupply,
} from './VehicleProfiling.helpers';
import LocalTexts from './VehicleProfilingTexts.json';

const LocalT = new MDText(LocalTexts);

const renderLocalT = (path: string, str: string) => LocalT.translate(`confirmDetails.sections.${path}.${str}`);

const {
  CONDITION, CONTACTS, PHOTOS, PRICE, PROFILE, SERVICE_HISTORY, VEHICLE,
} = CONFIRM_DETAILS_SECTIONS;

export const useProfilingUrlPath = () => {
  const { params } = useRouteMatch<{ vrm: string }>();
  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);

  return `/${params?.vrm || vehicleLookup?.vrm}${PROFILING_PATH.PROFILE}`;
};

export const useIsProfileConfirmed = () => {
  const {
    premiumOfferData,
  } = useVehicleProfilingData();

  return Boolean(premiumOfferData?.profileConfirmationDetails?.profileDetailsConfirmedAt);
};

/**
 * hook to display the error toast with refresh button
 * */

export const handleRefresh = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault();
  window.location.reload();
};

const refreshLink = (
  <a href="#refresh" onClick={handleRefresh} rel="noreferrer" target="_blank">
    {LocalT.translate('servicePage.error.link')}
  </a>
);

export const ERROR_TOAST = {
  CONTENT: LocalT.translate('servicePage.error.paragraph', { link: refreshLink }),
  CONTENT_VEHICLE_DETAILS_CONFIRMATION: LocalT.translate('confirmDetails.error.paragraph', { link: refreshLink }),
  HEADING: LocalT.translate('servicePage.error.heading'),
};

type ErrorToastPropsTypes = {
  content?: React.ReactNode;
  heading?: React.ReactNode;
  persistent?: boolean;
};

export const useGeneralErrorToast = () => {
  const showErrorToast = useCallback((props?: ErrorToastPropsTypes) => {
    const { content, heading, persistent } = props ?? {};

    if (toast) {
      toast({
        content: content ?? ERROR_TOAST.CONTENT,
        heading: heading ?? ERROR_TOAST.HEADING,
        persistent,
        variant: INFOBOX_VARIANT.ERROR,
      });
    }
  }, []);

  return { showErrorToast };
};

type ConfirmVehiclePayload = Record<string, boolean> & {
  meta?: Partial<Pick<PremiumOffer, 'enquiryTier' | 'priceToConfirm'>>;
}

export const useConfirmVehicle = () => {
  const history = useHistory();
  const {
    premiumOfferData, prepCostReconfirm, profilingUrlPath, update,
  } = useVehicleProfilingContext();
  const { id, prepCosts, profileConfirmationDetails, sellerWillAcceptPrice, vehicleDetails } = premiumOfferData ?? {};
  const { vrm } = vehicleDetails ?? {};

  const { showErrorToast } = useGeneralErrorToast();

  const [isLoading, setIsLoading] = useState(false);
  const isReadyForValuation = (data: ProfileConfirmationDetails) =>
    Object.values(UPDATED_VALUATION_REQUIREMENTS).every(
      ((confirmType) => data?.[confirmType] && !!data[confirmType]),
    );

  const confirmVehicle = async (
    data: ConfirmVehiclePayload,
    type: string | string[],
    cb?: () => void,
    extra?: { newEstimatedSalePrice?: number },
  ) => {
    setIsLoading(true);
    try {
      const res: ProfileConfirmationDetails = await putEnquiryConfirmation(id, { ...data });

      const newProfileConfirmationDetails: ProfileConfirmationDetails = {
        ...profileConfirmationDetails, ...res,
      };

      update({
        premiumOfferData: {
          profileConfirmationDetails: newProfileConfirmationDetails,
          ...(extra?.newEstimatedSalePrice && { sellerWillAcceptPrice: extra?.newEstimatedSalePrice }),
        },
      } as Partial<VehicleProfilingState>);

      if (prepCostReconfirm && isReadyForValuation(newProfileConfirmationDetails)) {
        history.push(`${profilingUrlPath}${PROFILING_PATH.UPDATED_VALUATION}`);
      } else {
        cb?.();
      }
    } catch (err: any) {
      showErrorToast();
      window?.Sentry?.captureException?.(new Error(err), {
        extra: {
          confirmVehicleData: {
            ...data,
            newEstimatedSalePrice: extra?.newEstimatedSalePrice ?? null,
            prepCostsValue: prepCosts ?? null,
            sellerWillAcceptPriceValue: sellerWillAcceptPrice ?? null,
            type,
          },
          vehicle: {
            dpEnquiryId: id,
          },
        },
        tags: {
          fetch: 'Put Enquiry Confirmation',
        },
      });
      if (err?.message === CONFIRM_PRICE_EXPIRED_ERROR_MSG) {
        SP_UPDATED_VALUATION.CONFIRM_PRICE_EXPIRED({ vrm: vrm! });
        window.location.href = `/${vrm}`; // Force a page load
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { confirmVehicle, isLoading };
};

export const useRedirectToTimeline = () => {
  const { pathname } = useLocation();
  const profilingUrlPath = useProfilingUrlPath();
  const isProfileConfirmed = useIsProfileConfirmed();
  const { metaData, premiumOfferData, resupply } = useVehicleProfilingData();

  const { profilingStatus } = premiumOfferData ?? {};
  const { isPhotoResupplyComplete } = metaData ?? {};
  const { slug } = premiumOfferData?.state ?? {};

  const vehicleProfilePath = `${profilingUrlPath}${PROFILING_PATH.VEHICLE_PROFILE}`;
  const serviceHistoryPath = `${profilingUrlPath}${PROFILING_PATH.SERVICE}`;
  const serviceHistoryUploadPath = `${profilingUrlPath}${PROFILING_PATH.SERVICE_UPLOAD}`;
  const photosPath = `${profilingUrlPath}${PROFILING_PATH.PHOTOS}`;
  const timelinePath = `${profilingUrlPath}${PROFILING_PATH.TIMELINE}`;

  const nonRestrictedConfirmedPaths = [vehicleProfilePath, timelinePath];

  const isStatusRejectedOrPostQa = (isStatusRejectedTimeline(slug) || isStatusPostQaTimeline(slug));

  if (!isPhotoResupplyComplete) {
    if (profilingStatus && isServiceResupply(profilingStatus)) {
      nonRestrictedConfirmedPaths.push(profilingUrlPath, serviceHistoryPath, serviceHistoryUploadPath);
    }

    if (profilingStatus && isPhotosResupply(profilingStatus)) {
      nonRestrictedConfirmedPaths.push(profilingUrlPath, photosPath);
    }
  }

  /**
   * If the enquiry is not resupply and the user is on a restricted path,
   * and the profile is either confirmed or the enquiry status
   * is set to a post QA status or a rejected status (excludes: customer_lost).
   * Then redirected to timeline.
   *
   * * We also check the status as an edge case, just in case a seller is put into a
   * * post-QA status without having their profile confirmed. This prevents
   * * them from editing their profile; this has happened in the past due to
   * * validation issues on the dashboard.
   *
   * * customer_lost is excluded as seller are often put into this status
   * * before reaching full_details_wait.
   */
  const timelineIsNotResupply = !resupply && !nonRestrictedConfirmedPaths.includes(pathname)
  && (isProfileConfirmed || isStatusRejectedOrPostQa);

  // If the enquiry is resupply and the user is on a restricted path,
  // then redirected to timeline.
  const timelineIsResupply = resupply && !nonRestrictedConfirmedPaths.includes(pathname);

  const redirectToTimeline = timelineIsNotResupply || timelineIsResupply;

  return redirectToTimeline;
};

export const useTimelineRestrictedToAccess = () => {
  const { pathname } = useLocation();

  const profilingUrlPath = useProfilingUrlPath();
  const { premiumOfferData } = useVehicleProfilingData();
  const isProfileConfirmed = useIsProfileConfirmed();

  const { slug } = premiumOfferData?.state || {};

  const timelinePath = `${profilingUrlPath}${PROFILING_PATH.TIMELINE}`;

  const isStatusRejectedOrPostQa = (isStatusRejectedTimeline(slug) || isStatusPostQaTimeline(slug));

  const timelineRestrictedToAccess = !isProfileConfirmed && pathname === timelinePath && !isStatusRejectedOrPostQa;

  return timelineRestrictedToAccess;
};

const details = {
  contact: 'contactDetails',
  wizard: 'wizardDetails',
};

export const useDataSections = (): SectionsProps[] => {
  const {
    premiumOfferData,
  } = useVehicleProfilingData();
  const {
    profileConfirmationDetails,
    vehicleDetails,
  } = premiumOfferData || {};
  const {
    hasProofOfPurchase,
    hasV5LogBook,
    keeperStartDate,
  } = vehicleDetails || {};

  const mustHaveProofOfPurchase = isMustHaveProofOfPurchase(keeperStartDate);
  const vehicleTitle = LocalT.translate('confirmDetails.sections.vehicle.title');
  const { wizard } = details;

  return [{
    data: [
      { tableName: renderLocalT(wizard, 'v5c'), value: hasV5LogBook },
      ...(mustHaveProofOfPurchase ? [{ tableName: renderLocalT(wizard, 'proof'), value: hasProofOfPurchase }] : []),
    ],
    desc: LocalT.translate('confirmDetails.sections.vehicle.desc'),
    isCompleted: !!profileConfirmationDetails?.vehicleDetailsConfirmedAt,
    isDependencyCompleted: !!profileConfirmationDetails?.contactDetailsConfirmedAt,
    title: vehicleTitle,
    type: VEHICLE,
  }];
};

export const useContactsSections = (): SectionsProps[] => {
  const { userState: user } = useContext(UserContext);
  const { email, name: userName, phone } = user ?? {};
  const { premiumOfferData, profilingUrlPath } = useVehicleProfilingData();
  const { profileConfirmationDetails } = premiumOfferData || {};
  const contactTitle = LocalT.translate('confirmDetails.sections.contact.title');
  const phoneFormatted = formatPhoneNumber(phone);
  const { contact } = details;

  const contactRoute = `${profilingUrlPath}${PROFILING_PATH.CONTACTS}`;

  return [{
    data: [
      { route: contactRoute, tableName: renderLocalT(contact, 'fullName'), value: userName },
      { route: contactRoute, tableName: renderLocalT(contact, 'email'), value: email },
      { route: contactRoute, tableName: renderLocalT(contact, 'phoneNumber'), value: phoneFormatted.pretty },
    ],
    desc: LocalT.translate('confirmDetails.sections.contact.desc'),
    isCompleted: !!profileConfirmationDetails?.contactDetailsConfirmedAt,
    isDependencyCompleted: true,
    title: contactTitle,
    type: CONTACTS,
  }];
};

export const useGetPhotos = () => {
  const { premiumOfferData } = useVehicleProfilingData();
  const [photos, setPhotos] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState<Error | null>(null);

  useEffect(() => {
    getVehiclePhotos(premiumOfferData?.id)
      .then((response) => {
        const { data } = response ?? {};

        if (Array.isArray(data)) {
          setPhotos(data);
        } else {
          setIsError(new Error('Invalid `getVehiclePhotos` response format'));
        }
      })
      .catch((err: any) => {
        setIsError(err);
        timeoutFetchSentryGuard(err, () => window?.Sentry?.captureException(new Error(err)));
      }).finally(() => {
        setIsLoading(false);
      });
  }, [premiumOfferData?.id]);

  return { isError, isLoading, photos };
};

export const useSortedPhotos = (photos: Photos) => {
  const [
    { conditionPhotos, resupplyPhotos, serviceHistoryPhotos, vehiclePhotos }, setPhotos,
  ] = useState<ImageSections>({
    conditionPhotos: [],
    resupplyPhotos: [],
    serviceHistoryPhotos: [],
    vehiclePhotos: [],
  });

  useEffect(() => {
    if (!photos) {
      return;
    }
    const sortedPhotos = photos.reduce<ImageSections>((acc, x) => {
      switch (true) {
      case isRetakeStatus(x.status):
        acc.resupplyPhotos.push(x);
        break;
      case x.kind === SERVICE_PHOTOS_KEY && !isRetakeStatus(x.status):
        acc.serviceHistoryPhotos.push(x);
        break;
      case x.kind.includes('damage'):
        acc.conditionPhotos.push(x);
        break;
      default:
        acc.vehiclePhotos.push(x);
        break;
      }

      return acc;
    }, { conditionPhotos: [], resupplyPhotos: [], serviceHistoryPhotos: [], vehiclePhotos: [] });

    setPhotos(sortedPhotos);
  }, [photos]);

  return { conditionPhotos, resupplyPhotos, serviceHistoryPhotos, vehiclePhotos };
};

export const useGetSortedPhotos = () => {
  const { photos } = useGetPhotos();
  return useSortedPhotos(photos);
};

export const usePhotosSections = (photos: Photos) => {
  const { premiumOfferData } = useVehicleProfilingData();
  const { profileConfirmationDetails } = premiumOfferData || {};

  return (
    {
      desc: LocalT.translate('confirmDetails.sections.photo.desc'),
      isCompleted: !!profileConfirmationDetails?.vehiclePhotosConfirmedAt,
      isDependencyCompleted: !!profileConfirmationDetails?.vehicleDetailsConfirmedAt,
      photos,
      title: LocalT.translate('confirmDetails.sections.photo.title'),
    }
  );
};

export const useConditionSections = (photos: Photos) => {
  const { premiumOfferData } = useVehicleProfilingData();
  const { profileConfirmationDetails } = premiumOfferData || {};

  return (
    {
      desc: LocalT.translate('confirmDetails.sections.condition.desc'),
      isCompleted: !!profileConfirmationDetails?.vehicleConditionConfirmedAt,
      isDependencyCompleted: !!profileConfirmationDetails?.vehiclePhotosConfirmedAt,
      photos,
      title: LocalT.translate('confirmDetails.sections.condition.title'),
    }
  );
};

export const useServiceHistorySections = (photos: Photos) => {
  const { premiumOfferData } = useVehicleProfilingData();
  const { profileConfirmationDetails } = premiumOfferData || {};

  const data = getHistorySections(LocalTexts)
    .reduce<TableDataProps[]>((acc, [key, v]) => {
      if (key === SERVICE_UPLOAD_KEY) {
        return acc;
      }

      const section = {
        ...v,
        key,
        questions: { ...LocalTexts.servicePage.history.questions },
      };

      acc.push(section);

      return acc;
    }, []);

  return (
    {
      data,
      desc: LocalT.translate('confirmDetails.sections.serviceHistory.desc'),
      isCompleted: !!profileConfirmationDetails?.serviceHistoryConfirmedAt,
      isDependencyCompleted: !!profileConfirmationDetails?.vehicleConditionConfirmedAt,
      photos,
      title: LocalT.translate('confirmDetails.sections.serviceHistory.title'),
    }
  );
};

export const useVehicleInformation = (): SectionsProps[] => {
  const excludedConfirmationValues = Object.values(LocalTexts)
    .reduce<string[]>((acc, { excludedConfirmKeyName: key }: any) => (key ? [...acc, key] : acc), []);

  const vehicleSections = useSummarySections(excludedConfirmationValues);
  const dataSections = useDataSections();

  const {
    data: mainData, desc, isCompleted, isDependencyCompleted, title, type,
  } = dataSections[0];

  const vehicleData = vehicleSections.reduce<SectionDataProps[]>((acc, cur) => acc.concat(cur.data), []);

  const data = mainData.concat(vehicleData);

  return [{
    data,
    desc,
    isCompleted,
    isDependencyCompleted,
    title,
    type,
  }];
};

export const useGetButtonProps = () => {
  const breakpoints = useBreakpoint();
  const { breakpointMobile } = breakpoints.maxWidth;
  const history = useHistory();
  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);
  const { email, name, phone } = useUserPartial();
  const { prepCostReconfirm } = useVehicleProfilingContext();
  const { updateUserInfoAction } = useUpdateInfoAction();

  const { confirmVehicle, isLoading } = useConfirmVehicle();

  const sections = useSummarySections();
  const { showErrorToast } = useGeneralErrorToast();
  const { resendWizardDetails } = useResendVehicleDetails(sections);

  const getButtonProps = (type: string, payload = {}, extra = {}) => {
    const renderVehicleDetailsButtonText = () => {
      if (breakpointMobile) {
        return LocalT.translate('confirmDetails.sections.wizardDetails.buttonOnMobile');
      }

      return LocalT.translate('confirmDetails.sections.wizardDetails.button');
    };

    const contactDetailsButtonText = breakpointMobile ? LocalT.translate('confirmDetails.sections.contactDetails.buttonOnMobile') : LocalT.translate('confirmDetails.sections.contactDetails.button');

    const vehicleDetailsButtonText = renderVehicleDetailsButtonText();

    const isFinalConfirmation = type === PRICE;

    const data = isFinalConfirmation ? {
      [PROFILE]: true, [type]: true, ...payload,
    } : { [type]: true };

    const onConfirm = () => {
      if (prepCostReconfirm && history.location.pathname.includes(PROFILING_PATH.CONFIRM)) {
        return;
      }

      if (isFinalConfirmation) {
        toast({
          content: LocalT.translate('confirmDetails.success'),
          variant: INFOBOX_VARIANT.SUCCESS,
        });
      }
      const path = isFinalConfirmation ? PROFILING_PATH.TIMELINE : PROFILING_PATH.UPDATED_VALUATION;

      const redirectPath = `/${vehicleLookup?.vrm}/profile${path}`;
      history.push(redirectPath);
    };

    const scrollToSection = () => {
      scrollToTopOfSection(type);
    };

    switch (type) {
    case CONTACTS:
      return {
        isLoading,
        onClick: async () => {
          confirmVehicle(data, type);
          await updateUserInfoAction({ email, name, phone });
        },
        title: contactDetailsButtonText,
      };

    case VEHICLE:
      return {
        isLoading,
        onClick: async () => {
          await resendWizardDetails({
            onError: () => showErrorToast({
              content: ERROR_TOAST.CONTENT_VEHICLE_DETAILS_CONFIRMATION,
            }),
            onSuccess: () => confirmVehicle(data, type, scrollToSection),
          });
        },
        title: vehicleDetailsButtonText,
      };

    case PHOTOS:
      return {
        isLoading,
        onClick: async () => {
          confirmVehicle(data, type, scrollToSection);
        },
        title: LocalT.translate('confirmDetails.sections.photosDetails.button'),
      };

    case CONDITION:
      return {
        isLoading,
        onClick: async () => {
          confirmVehicle(data, type, scrollToSection);
        },
        title: LocalT.translate('confirmDetails.sections.conditionDetails.button'),
      };

    case SERVICE_HISTORY:
      return {
        isLoading,
        onClick: async () => {
          confirmVehicle(data, type, onConfirm);
        },
        title: LocalT.translate('confirmDetails.sections.serviceHistoryDetails.button'),
      };

    case PRICE:
      return {
        isLoading,
        onClick: async () => {
          confirmVehicle(data, [type, PROFILE], onConfirm, extra);
        },
        title: LocalT.translate('updatedValuation.button'),
      };

    default:
      return null;
    }
  };

  return getButtonProps;
};

/**
 * Set local storage items with the dpEnquiryId as a suffix
 */
export const useProfilingLocalStorage = () => {
  const { vehicleLookupState: vehicleLookup } = useContext(VehicleLookupContext);
  const { dpEnquiryId } = vehicleLookup?.enquiry ?? {};

  const setItem = (key: string, value: any) => {
    safeLocalStorage.setItem(`${key}-${dpEnquiryId}`, value);
  };

  const removeItem = (key: string) => {
    safeLocalStorage.removeItem(`${key}-${dpEnquiryId}`);
  };

  const getItem = useCallback((key: string, defaultValue?: any) => safeLocalStorage.getItem(`${key}-${dpEnquiryId}`, defaultValue), [dpEnquiryId]);

  return { getItem, removeItem, setItem };
};
